import React, { FC, useEffect } from 'react';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import MoreIcon from '@mui/icons-material/MoreVert';
import translate, { getLangKey } from '@translate';
import Typography from 'src/components/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { RootStoreType } from 'src/types/store';
import { ProviderType } from 'src/types/provider';
import checkOrChangeProvider from 'src/utils/checkOrChangeProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import { logoutFetch } from 'src/resources/login/reducer';
import UserMenu from './UserMenu';
import NotificationsMenu from './NotificationsMenu';
import MUIMenu, { MenuProps } from '@mui/material/Menu';
import { styled } from '@mui/material/styles';
import { colors, sizes } from 'src/themes/default';
import { Avatar } from '@mui/material';
import { AvatarIcon } from 'src/components/SkladonIcon/Icons';
import IconButton from 'src/components/IconButton';
import Button from 'src/components/Button';
import { deleteNotificationsFetch } from 'src/resources/notifications/reducer';
import { BellIcon, BlueInfoIcon, OrangeWarningIcon, RedErrorIcon } from 'src/components/SkladonIcon/SmallIcons';
import { disabbleMessages } from 'src/resources/alertMessages/reducer';
import LangSelect from 'src/components/LangSelect';
import { applicationMessageTypesColors } from '../../../resources/constantsCodes/constants';
import { getApplicationStatusMessagesActiveListFetch } from '../../../resources/applicationStatusMessagesList/reducer';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { formatDate } from '../../../components/History/functions';
import {getProviderStatusColor} from "../../../utils/providerStatusColorMap";

const Menu = styled(MUIMenu)<MenuProps>(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: sizes.radius,
        boxShadow: '0px 4px 8px 0px #56495929; 0px 0px 2px 0px #938B950A; 0px 1px 2px 0px #847B880F;',

        '& .MuiList-root': {
            paddingTop: 0,
            paddingBottom: 0
        }
    }
}));

const AppBarIcons = styled('div')({
    display: 'inline-block',

    '& button svg': {
        fontSize: sizes.icon_button_FS
    },

    '& button .MuiButton-startIcon svg': {
        fontSize: sizes.icon_button_FS
    }
});

const LangBlock = styled('div')(({ theme }) => ({
    paddingRight: '20px',
    paddingTop: '12px',
    paddingBottom: '12px',
    position: 'relative',
    display: 'inline-flex',

    '& > div > div:first-child': {
        marginBottom: '0px'
    },

    [theme.breakpoints.up('sm')]: {
        marginRight: '-20px'
    },
    [theme.breakpoints.down('sm')]: {
        position: 'absolute',
        right: '0px',
        paddingTop: '0px',
        paddingBottom: '0px'
    }
}));

const LAST_POP_READ = 'lastPopupRead';
const AppBarIconsContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const allowedNotificationTypes = ['banner', 'both'];
    const popupNotificationTypes = ['popup', 'both'];
    const userData = useSelector((state: RootStoreType) => state.user.data);
    const providers = userData?.providers || [];
    const provider = useSelector((state: RootStoreType) => state.provider.data);
    const notificationsList = useSelector((state: RootStoreType) => state.notifications.notificationsList);
    const applicationStatusMessagesList = useSelector((state: RootStoreType) => state.applicationStatusMessagesList.dataActive);
    const langKey = getLangKey();
    const informationMessages = applicationStatusMessagesList ? applicationStatusMessagesList.filter((item) => item.LANG === langKey && item.TYPE === 'info' && item.ACTIVE && allowedNotificationTypes.includes(item.NOTIFICATION_TYPE!)) : [];
    const warningMessages = applicationStatusMessagesList ? applicationStatusMessagesList.filter((item) => item.LANG === langKey && item.TYPE === 'warning' && item.ACTIVE && allowedNotificationTypes.includes(item.NOTIFICATION_TYPE!)) : [];
    const errorMessages = applicationStatusMessagesList ? applicationStatusMessagesList.filter((item) => item.LANG === langKey && item.TYPE === 'error' && item.ACTIVE && allowedNotificationTypes.includes(item.NOTIFICATION_TYPE!)) : [];
    const lastPopup = Number(localStorage.getItem(LAST_POP_READ));
    const popupMessages = applicationStatusMessagesList ? applicationStatusMessagesList.filter((item) => {
        return (item.ID && lastPopup < item.ID) && item.LANG === langKey && item.ACTIVE && popupNotificationTypes.includes(item.NOTIFICATION_TYPE!);
    }) : [];
    const hasApplicationStatusMessages = !!informationMessages.length || !!warningMessages.length || !!errorMessages.length;
    const [userAnchorEl, setUserAnchorEl] = React.useState<null | HTMLElement>(null);
    const [notificationsAnchorEl, setNotificationsAnchorEl] = React.useState<null | HTMLElement>(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState<null | HTMLElement>(null);
    const [lastPopupRead, setLastPopupRead] = React.useState<number>(lastPopup);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setUserAnchorEl(event.currentTarget);
    };

    const handleNotificationsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setNotificationsAnchorEl(event.currentTarget);
    };

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const handleChangeProvider = (providerItem: ProviderType) => {
        if (provider && providerItem.ESHOP_KEY) {
            if (checkOrChangeProvider(providers, provider, 'ESHOP_KEY', providerItem.ESHOP_KEY)) {
                dispatch(disabbleMessages());
            }
        }
        handleMenuClose();
    };

    const handleProfile = () => {
        navigate('/profil');
        handleMenuClose();
    };

    const handleMessagesClick = () => {
        navigate('/');
    };

    const handlLogout = () => {
        dispatch(logoutFetch({ navigate }));
        handleMenuClose();
    };

    const handleMenuClose = () => {
        setUserAnchorEl(null);
        setNotificationsAnchorEl(null);
        setMobileMoreAnchorEl(null);
    };

    const handleDelete = (ids: string[]) => {
        if (ids.length) {
            dispatch(deleteNotificationsFetch(ids));
        }
    };

    const handleConfirmPopup = () => {
        localStorage.setItem(LAST_POP_READ, String(popupMessages[0].ID));
        setLastPopupRead(popupMessages[0].ID!);
    };

    const location = useLocation();

    useEffect(() => {
        if (!location.pathname.includes('application_status_messages')) {
            dispatch(getApplicationStatusMessagesActiveListFetch());
        }
    }, []);

    const getBorderColor = (type: string) => {
        switch (type) {
            case 'info':
                return applicationMessageTypesColors.info;
            case 'warning':
                return applicationMessageTypesColors.warning;
            case 'error':
                return applicationMessageTypesColors.error;
            default:
                return colors.primary_O;
        }
    };

    const badge = (
        <Badge
            badgeContent={notificationsList.length}
            color="error"
            sx={{
                '& .MuiBadge-badge': {
                    marginTop: '13px',
                    marginRight: '5px',
                    fontSize: '10px',
                    height: '16px',
                    minWidth: '16px',
                    lineHeight: '16px',
                    backgroundColor: colors.primary_O
                },
                '& svg': {
                    height: '32px',
                    width: 'auto'
                }
            }}
        >
            <BellIcon />
        </Badge>
    );

    const informationBadge = (
        <Badge
            badgeContent={informationMessages.length}
            color="error"
            sx={{
                '& .MuiBadge-badge': {
                    marginTop: '-5px',
                    marginRight: '-5px',
                    fontSize: '10px',
                    height: '16px',
                    minWidth: '16px',
                    lineHeight: '16px',
                    backgroundColor: applicationMessageTypesColors.info
                },
                '& svg': {
                    height: '22px',
                    width: 'auto'
                }
            }}
        >
            <BlueInfoIcon />
        </Badge>
    );

    const warningBadge = (
        <Badge
            badgeContent={warningMessages.length}
            color="error"
            sx={{
                '& .MuiBadge-badge': {
                    marginTop: '-5px',
                    marginRight: '-5px',
                    fontSize: '10px',
                    height: '16px',
                    minWidth: '16px',
                    lineHeight: '16px',
                    backgroundColor: applicationMessageTypesColors.warning
                },
                '& svg': {
                    height: '22px',
                    width: 'auto'
                }
            }}
        >
            <OrangeWarningIcon />
        </Badge>
    );

    const errorBadge = (
        <Badge
            badgeContent={errorMessages.length}
            color="error"
            sx={{
                '& .MuiBadge-badge': {
                    marginTop: '-5px',
                    marginRight: '-5px',
                    fontSize: '10px',
                    height: '16px',
                    minWidth: '16px',
                    lineHeight: '16px',
                    backgroundColor: applicationMessageTypesColors.error
                },
                '& svg': {
                    height: '22px',
                    width: 'auto',
                    color: 'red !important'
                }
            }}
        >
            <RedErrorIcon />
        </Badge>
    );

    const mobileMenuId = 'primary-search-account-menu-mobile';
    const renderMobileMenu = (
        <Menu
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right'
            }}
            id={mobileMenuId}
            keepMounted
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
            }}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
        >
            <MenuItem onClick={handleNotificationsMenuOpen}>
                <IconButton size="large" color="inherit" noBackground>
                    {badge}
                </IconButton>
                <p>{translate('appBar.notifications')}</p>
            </MenuItem>
            <MenuItem onClick={handleProfileMenuOpen}>
                <IconButton size="large" color="inherit">
                    <AvatarIcon />
                </IconButton>
                <p>{translate('appBar.userSettings')}</p>
            </MenuItem>
            <MenuItem>
                <LangBlock>
                    <LangSelect />
                </LangBlock>
            </MenuItem>
        </Menu>
    );

    return (
        <AppBarIcons>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                {informationMessages.length > 0 && (
                    <IconButton
                        size="large"
                        color="inherit"
                        onClick={handleMessagesClick}
                        tooltiop={translate('appBar.infoMessages')}
                        // noBackground
                        sx={{
                            height: '34px',
                            margin: '15px 5px',
                            border: `3px solid ${applicationMessageTypesColors.info}`,
                            borderRadius: '4px',
                            padding: '5px',
                            '&:hover': {
                                backgroundColor: colors.background_B1
                            }
                        }}
                    >
                        {informationBadge}
                    </IconButton>
                )}
                {warningMessages.length > 0 && (
                    <IconButton
                        size="large"
                        color="inherit"
                        onClick={handleMessagesClick}
                        tooltiop={translate('appBar.warningMessages')}
                        // noBackground
                        sx={{
                            height: '34px',
                            margin: '15px 5px',
                            border: `3px solid ${applicationMessageTypesColors.warning}`,
                            borderRadius: '4px',
                            padding: '5px',
                            '&:hover': {
                                backgroundColor: colors.background_B1
                            }
                        }}
                    >
                        {warningBadge}
                    </IconButton>
                )}
                {errorMessages.length > 0 && (
                    <IconButton
                        size="large"
                        color="inherit"
                        onClick={handleMessagesClick}
                        tooltiop={translate('appBar.errorMessages')}
                        // noBackground
                        sx={{
                            height: '34px',
                            margin: '15px 5px',
                            border: `3px solid ${applicationMessageTypesColors.error}`,
                            borderRadius: '4px',
                            padding: '5px',
                            '&:hover': {
                                backgroundColor: colors.background_B1
                            }
                        }}
                    >
                        {errorBadge}
                    </IconButton>
                )}
                <IconButton
                    size="large"
                    color="inherit"
                    onClick={handleNotificationsMenuOpen}
                    tooltiop={translate('appBar.notifications')}
                    // noBackground
                    sx={{
                        height: '44px',
                        margin: '10px 0px',
                        padding: '7px',
                        '&:hover': {
                            backgroundColor: colors.background_B1
                        }
                    }}
                >
                    {badge}
                </IconButton>
                <Button
                    startIcon={userData?.PHOTO_HASH ? <Avatar src={userData.PHOTO_HASH} /> : <AvatarIcon />}
                    onClick={handleProfileMenuOpen}
                    color="inherit"
                    variant="text"
                    tooltiop={translate('appBar.userSettings')}
                    sx={{
                        height: '44px',
                        margin: '10px 0px',
                        padding: '7px',
                        paddingRight: '15px',
                        marginLeft: '7px',
                        '&:hover': {
                            backgroundColor: colors.background_B1
                        }
                    }}
                    // noBackground
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: getProviderStatusColor(provider?.STATUS, true),
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {provider?.COMPANY_NAME}
                    </Typography>
                </Button>

                <LangBlock>
                    <LangSelect />
                </LangBlock>
            </Box>
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <IconButton size="large" onClick={handleMobileMenuOpen} color="inherit">
                    <MoreIcon />
                </IconButton>
            </Box>
            {renderMobileMenu}
            {provider && (
                <>
                    <UserMenu
                        anchorEl={userAnchorEl}
                        onClose={handleMenuClose}
                        providers={providers}
                        provider={provider}
                        handleChangeProvider={handleChangeProvider}
                        handleProfile={handleProfile}
                        handlLogout={handlLogout}
                    />
                    <NotificationsMenu
                        anchorEl={notificationsAnchorEl}
                        onClose={handleMenuClose}
                        onDelete={handleDelete}
                        handleChangeProvider={handleChangeProvider}
                        notificationsList={notificationsList}
                        providers={providers}
                        provider={provider}
                    />
                </>
            )}
            {popupMessages.length > 0 && (
                <ConfirmDialog
                    title={popupMessages[0].TITLE}
                    content={popupMessages[0].TEXT}
                    action={handleConfirmPopup}
                    onClose={handleConfirmPopup}
                    maxWidth={'lg'}
                    customContent={(data, setData) => (
                        <div>
                            <div dangerouslySetInnerHTML={{ __html: popupMessages[0].TEXT! }} />
                            <div><br /></div>
                            <div
                                style={{fontSize: '0.8rem'}}
                                dangerouslySetInnerHTML={{ __html: `<i>${String(formatDate(popupMessages[0].CREATED_AT!.toString()))}</i>` }}
                            />
                        </div>
                    )}
                    hideCancel={true}
                    fullWidth={true}
                    sx={{
                        '& .MuiPaper-root': {
                            border: `3px solid ${getBorderColor(popupMessages[0].TYPE || 'info')}`
                        }
                    }}
                />
            )}
        </AppBarIcons>
    )
        ;
};

export default AppBarIconsContainer;
